/**
 * Translation Registry - Centralized translation management system
 * 
 * Provides centralized registration and retrieval of translations with support for:
 * - Module-based translation registration
 * - Page-specific translations
 * - Common/shared translations
 * - Backward compatibility with window.TRAN
 * - Fast lookup via flat cache
 */
(function(window) {
    'use strict';

    const TranslationRegistry = {
        // Internal storage: { moduleName: { key: { EN: '...', RU: '...', DE: '...' } } }
        _translations: {},
        
        // Flat cache for fast lookup: { key: { EN: '...', RU: '...', DE: '...' } }
        _cache: {},
        
        /**
         * Register translations from a module or page
         * @param {string} moduleName - Unique module identifier (e.g., 'auth', 'onboarding', 'common', 'search')
         * @param {Object} translations - Translations object: { key: { EN: '...', RU: '...', DE: '...' } }
         * @param {boolean} merge - If true, merge with existing translations for this module
         * @returns {boolean} Success status
         */
        register: function(moduleName, translations, merge = false) {
            if (!moduleName || typeof moduleName !== 'string') {
                console.warn('[TranslationRegistry] Invalid module name:', moduleName);
                return false;
            }
            
            if (!translations || typeof translations !== 'object') {
                console.warn('[TranslationRegistry] Invalid translations object for module:', moduleName);
                return false;
            }
            
            // Store module-specific translations
            if (!this._translations[moduleName]) {
                this._translations[moduleName] = {};
            }
            
            if (merge) {
                Object.assign(this._translations[moduleName], translations);
            } else {
                this._translations[moduleName] = translations;
            }
            
            // Update flat cache for fast lookup (later registrations override earlier ones)
            Object.assign(this._cache, translations);
            
            // Update global window.TRAN for backward compatibility (deprecated, use TranslationRegistry directly)
            if (!window.TRAN) {
                window.TRAN = {};
            }
            Object.assign(window.TRAN, translations);
            
            return true;
        },
        
        /**
         * Get translation for specified language with fallback
         * @param {string} key - Translation key
         * @param {string} lang - Language code (optional, uses current language if not provided)
         * @returns {string} Translated string or key if not found
         */
        translate: function(key, lang = null) {
            if (!key || typeof key !== 'string') {
                return key;
            }
            
            // Get current language if not specified
            const currentLang = lang || this._getCurrentLanguage();
            
            // Try to get translation from cache
            if (this._cache[key]) {
                const translation = this._cache[key];
                
                // Try current language
                if (translation[currentLang] && translation[currentLang] !== '') {
                    return translation[currentLang];
                }
                
                // Fallback to EN
                if (translation['EN'] && translation['EN'] !== '') {
                    return translation['EN'];
                }
                
                // Fallback to first available language
                const availableLang = Object.keys(translation).find(l => translation[l] && translation[l] !== '');
                if (availableLang) {
                    return translation[availableLang];
                }
            }
            
            // Debug: log missing translations in non-prod
            // Disabled to reduce console noise
            // if (window.SERVER_ENV && !window.SERVER_ENV.isProd && !key.startsWith('_')) {
            //     console.debug(`[TranslationRegistry] Translation not found: key='${key}', lang='${currentLang}', cache has ${Object.keys(this._cache).length} keys`);
            // }
            
            // Fallback to key itself
            return key;
        },
        
        /**
         * Get all translations for a specific module
         * @param {string} moduleName - Module name
         * @returns {Object} Module translations or empty object
         */
        getModuleTranslations: function(moduleName) {
            return this._translations[moduleName] || {};
        },
        
        /**
         * Get all translations (for Python export)
         * @returns {Object} All translations in format { key: { EN: '...', RU: '...', DE: '...' } }
         */
        getAllTranslations: function() {
            return this._cache;
        },
        
        /**
         * Load translations from JSON file via fetch() with versioning support
         * @param {string} jsonPath - Path to JSON file (e.g., '/static/js/translations/common/translations.json')
         * @param {string} moduleName - Module name for registration
         * @returns {Promise<boolean>} Success status
         */
        loadFromJSON: async function(jsonPath, moduleName) {
            try {
                // Get versioned URL if cache manifest is available
                let url = jsonPath;
                if (window.getVersionedUrl && typeof window.getVersionedUrl === 'function') {
                    url = window.getVersionedUrl(jsonPath);
                    if (url === jsonPath && window.__cacheManifest && window.__cacheManifest.modules) {
                        // Debug: check if path exists in manifest
                        console.log('[TranslationRegistry] Path not found in manifest:', jsonPath, 'Available keys:', Object.keys(window.__cacheManifest.modules).filter(k => k.includes('translations')));
                    }
                } else if (window.__cacheManifest && window.__cacheManifest[jsonPath]) {
                    url = jsonPath + '?v=' + window.__cacheManifest[jsonPath];
                } else if (window.__cacheManifest && window.__cacheManifest.modules && window.__cacheManifest.modules[jsonPath]) {
                    const etag = typeof window.__cacheManifest.modules[jsonPath] === 'string' 
                        ? window.__cacheManifest.modules[jsonPath] 
                        : window.__cacheManifest.modules[jsonPath].etag;
                    if (etag) {
                        url = jsonPath + '?v=' + etag;
                    }
                }
                
                const response = await fetch(url);
                if (!response.ok) {
                    console.warn(`[TranslationRegistry] Failed to load ${jsonPath}: ${response.status}`);
                    return false;
                }
                
                const translations = await response.json();
                if (translations && typeof translations === 'object') {
                    this.register(moduleName, translations);
                    return true;
                } else {
                    console.warn(`[TranslationRegistry] Invalid JSON format in ${jsonPath}`);
                    return false;
                }
            } catch (error) {
                console.warn(`[TranslationRegistry] Error loading ${jsonPath}:`, error);
                return false;
            }
        },
        
        /**
         * Load multiple translation JSON files
         * @param {Array<{path: string, module: string}>} files - Array of {path, module} objects
         * @returns {Promise<Array<boolean>>} Array of success statuses
         */
        loadFromJSONFiles: async function(files) {
            const results = await Promise.all(
                files.map(file => this.loadFromJSON(file.path, file.module))
            );
            return results;
        },
        
        /**
         * Load translations from legacy TRAN object (for migration)
         * @param {Object} legacyTRAN - Legacy TRAN object
         */
        loadLegacy: function(legacyTRAN) {
            if (legacyTRAN && typeof legacyTRAN === 'object') {
                this.register('legacy', legacyTRAN, true);
            }
        },
        
        /**
         * Get current language from LanguageManager or window
         * @private
         * @returns {string} Current language code (EN, RU, DE, etc.)
         */
        _getCurrentLanguage: function() {
            // Try LanguageManager first
            if (window.LanguageManager && typeof window.LanguageManager.getCurrentLanguage === 'function') {
                return window.LanguageManager.getCurrentLanguage();
            }
            
            // Try window.LANG
            if (window.LANG) {
                return window.LANG.toUpperCase();
            }
            
            // Try window.LANG_FROM_SERVER
            if (window.LANG_FROM_SERVER) {
                return window.LANG_FROM_SERVER.toUpperCase();
            }
            
            // Try window.LANG_BROWSER
            if (window.LANG_BROWSER) {
                return window.LANG_BROWSER.toUpperCase();
            }
            
            // Default to EN
            return 'EN';
        },
        
        /**
         * Check if a translation key exists
         * @param {string} key - Translation key
         * @returns {boolean} True if key exists
         */
        hasTranslation: function(key) {
            return key in this._cache;
        },
        
        /**
         * Clear all translations (for testing/debugging)
         */
        clear: function() {
            this._translations = {};
            this._cache = {};
            window.TRAN = {};
        }
    };
    
    // Export to global namespace
    window.TranslationRegistry = TranslationRegistry;
    
    // Legacy translations.js removed - translations now loaded from JSON files only
    
    // ES Module export for modern imports
    if (typeof window !== 'undefined') {
        window.translationRegistry = TranslationRegistry;
    }
    
})(window);

// ES Module export
if (typeof module !== 'undefined' && module.exports) {
    module.exports = window.TranslationRegistry || {};
}

